// v77.99.24 — Week View for CoParent Console.
//
// A 7-column × 16-hour time-grid that renders events as height-
// proportional blocks so parents can SEE durations at a glance —
// the prerequisite for any future drag-to-resize work.
//
// Design rules (driven by the user spec for predictable, mobile-
// friendly scheduling):
//
//   • TIME WINDOW: 06:00 → 22:00 (16 hours). Covers the realistic
//     parenting day. Events that fall outside this window still
//     render but get clamped to the edge with a small badge so
//     they're discoverable.
//
//   • SNAP-TO-15-MIN: The grid renders 15-minute sub-bands behind
//     each hour, so a future drag interaction can snap cleanly.
//     A tap on the grid creates an event whose start is rounded
//     DOWN to the nearest 15 min — never up — to match user
//     intent ("I tapped here, that should be the start").
//
//   • EVENT POSITION FORMULA (single source of truth):
//       top    = ((startMinSinceMidnight - WINDOW_START_MIN) / TOTAL_MIN) * 100  // %
//       height = max(MIN_EVENT_HEIGHT_PCT, durationMin / TOTAL_MIN * 100)        // %
//     Heights are floor-clamped so even a 5-minute event renders
//     as a visible band (we never want a parent's reminder to
//     vanish at this view's scale).
//
//   • CROSS-DAY EVENTS: An event spanning multiple days is rendered
//     once per day in the visible range, clipped to that day's
//     window. The underlying event id remains the same, so
//     interactions (open, edit) hit the original row.
//
//   • TOUCH UX:
//       - Whole-day columns scroll horizontally on narrow viewports
//         (overflow-x-auto on the grid wrapper).
//       - Event tap opens DayDetailDialog (same as Month view —
//         predictable behaviour across views).
//       - Empty-grid tap opens NewEventDialog with start pre-filled.
//
//   • TINT: Each event uses the same parent-tinted palette the
//     Month view uses (via typeClass()) so colours stay consistent
//     when the parent switches views.
//
//   • PRIVACY: This view consumes the *already-filtered* visible
//     events list. No new fetch, no new gate. Child-scope filtering
//     happens upstream in CalendarPage, which is the canonical
//     resolver per the v77.99.21 contract. Nothing new to verify
//     for permissions.
//
//   • RECURRENCE / OVERRIDES: Identical — the upstream
//     `visibleEvents` array has already expanded recurring series
//     into individual occurrences (look for "expand" in
//     CalendarPage.jsx). The Week view just positions what it's
//     given. No fork in business logic.

import { useMemo, useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Repeat, Lock, Eye, GripHorizontal, Clock } from "lucide-react";
import { toLocalInput } from "@/lib/format";

// Time-window config. Edit ONLY here.
const WINDOW_START_MIN = 6 * 60;   // 06:00
const WINDOW_END_MIN = 22 * 60;    // 22:00
const TOTAL_MIN = WINDOW_END_MIN - WINDOW_START_MIN;
const HOURS = Array.from(
  { length: (WINDOW_END_MIN - WINDOW_START_MIN) / 60 + 1 },
  (_, i) => WINDOW_START_MIN / 60 + i,
);
const SNAP_MIN = 15;
const MIN_EVENT_HEIGHT_PCT = 2.5; // ~24min worth of pixels at scale
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TYPE_LABEL = {
  custody: "Custody",
  pickup: "Pick-up",
  dropoff: "Drop-off",
  school: "School",
  extracurricular: "Extra",
  sports: "Sports",
  holiday: "Holiday",
  call: "Call",
  video_call: "Video",
  other: "Other",
};

/** Build the 7 ISO dates of the week containing `anchor`, Mon→Sun. */
function weekDatesFromAnchor(anchor) {
  const d = new Date(
    anchor.getFullYear(),
    anchor.getMonth(),
    anchor.getDate(),
  );
  // ISO week — Monday is day 0
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(d);
    x.setDate(d.getDate() + i);
    return x;
  });
}

/** Minutes since midnight for a local Date. */
function minSinceMidnight(d) {
  return d.getHours() * 60 + d.getMinutes();
}

/** Round minutes down to the nearest snap. */
function snapDown(m) {
  return Math.floor(m / SNAP_MIN) * SNAP_MIN;
}

/** Format minutes-since-midnight as HH:MM. */
function fmtTime(m) {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/** Same-day key used throughout CalendarPage. */
function dayKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * Per-day, clamped event blocks for the visible week.
 * Returns { 'Y-M-D': [{ ev, topPct, heightPct, isClippedStart, isClippedEnd }] }.
 */
function buildWeekBlocks(weekDates, events) {
  const byDay = {};
  weekDates.forEach((d) => {
    byDay[dayKey(d)] = [];
  });
  events.forEach((ev) => {
    const start = new Date(ev.start);
    const end = ev.end ? new Date(ev.end) : new Date(start.getTime() + 60 * 60 * 1000);
    // Iterate each day this event covers, clipping into the window.
    weekDates.forEach((d) => {
      const dayStart = new Date(d);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(d);
      dayEnd.setHours(23, 59, 59, 999);
      if (end < dayStart || start > dayEnd) return;
      const sMin = Math.max(WINDOW_START_MIN, minSinceMidnight(start < dayStart ? dayStart : start));
      const eMin = Math.min(WINDOW_END_MIN, minSinceMidnight(end > dayEnd ? dayEnd : end));
      if (eMin <= WINDOW_START_MIN || sMin >= WINDOW_END_MIN) return;
      const topPct = ((sMin - WINDOW_START_MIN) / TOTAL_MIN) * 100;
      const heightPct = Math.max(
        MIN_EVENT_HEIGHT_PCT,
        ((eMin - sMin) / TOTAL_MIN) * 100,
      );
      byDay[dayKey(d)].push({
        ev,
        topPct,
        heightPct,
        isClippedStart: start < dayStart || minSinceMidnight(start) < WINDOW_START_MIN,
        isClippedEnd: end > dayEnd || minSinceMidnight(end) > WINDOW_END_MIN,
      });
    });
  });
  return byDay;
}

export function WeekView({
  // Navigation
  weekOffset,
  setWeekOffset,
  // Data
  visibleEvents,
  // Helpers (reused from CalendarPage so styling stays identical)
  typeClass,
  parentIdx,
  childSwatches,
  // Callbacks
  onEventClick,
  onEmptySlotClick,
  // v77.99.25 — Drag-to-resize callback.
  // Called when the user finishes dragging an event's bottom handle.
  // The Week View calculates the new end time (snapped to 15 min) and
  // hands the parent: { ev, newEndIso }. CalendarPage routes that into
  // the existing EditOccurrenceDialog (form pre-filled with the new
  // end) so the recurrence-scope picker remains the single save path
  // — preserves audit history and recurrence logic unchanged.
  onEventResize,
  // Permissions
  isMediator,
}) {
  // Anchor week = `monday-of-this-week` + weekOffset weeks
  const weekDates = useMemo(() => {
    const today = new Date();
    const todayMonday = new Date(today);
    const dow = (today.getDay() + 6) % 7;
    todayMonday.setDate(today.getDate() - dow + weekOffset * 7);
    return weekDatesFromAnchor(todayMonday);
  }, [weekOffset]);

  const blocks = useMemo(
    () => buildWeekBlocks(weekDates, visibleEvents),
    [weekDates, visibleEvents],
  );

  const weekLabel = useMemo(() => {
    const a = weekDates[0];
    const b = weekDates[6];
    const sameMonth = a.getMonth() === b.getMonth();
    const fmtDay = (d) => d.toLocaleDateString(undefined, { day: "numeric" });
    const fmtMonth = (d) =>
      d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    return sameMonth
      ? `${fmtDay(a)} – ${fmtDay(b)} ${fmtMonth(b)}`
      : `${fmtDay(a)} ${a.toLocaleDateString(undefined, { month: "short" })} – ${fmtDay(b)} ${fmtMonth(b)}`;
  }, [weekDates]);

  // v77.99.28 — Live "now" tracking.
  //
  // A `Now` line slices today's column at the current minute so
  // parents see exactly where they are in the day — and a small
  // "Jump to now" pill in the header scroll-snaps the window so
  // that line is in view. Useful when the visible week contains
  // today AND the current time falls inside the 06:00-22:00 window
  // (otherwise the line and the pill both hide — no jump target).
  //
  // We re-read the clock every 60 seconds so the line never drifts
  // by more than a minute, and use a ref on the line itself so the
  // pill click can call scrollIntoView() without any DOM lookup.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const nowLineRef = useRef(null);

  const todayIndex = useMemo(() => {
    const todayStr = new Date().toDateString();
    return weekDates.findIndex((d) => d.toDateString() === todayStr);
  }, [weekDates]);

  const nowMinSinceMidnight = now.getHours() * 60 + now.getMinutes();
  const isNowInWindow =
    nowMinSinceMidnight >= WINDOW_START_MIN &&
    nowMinSinceMidnight <= WINDOW_END_MIN;
  // Pill shows only when today IS in this visible week AND the
  // current time is something the grid can actually point at.
  const showNowPill = todayIndex !== -1 && isNowInWindow;
  const nowTopPct = isNowInWindow
    ? ((nowMinSinceMidnight - WINDOW_START_MIN) / TOTAL_MIN) * 100
    : null;

  const handleJumpToNow = () => {
    if (!nowLineRef.current) return;
    nowLineRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Compact label for the pill — e.g. "Now · 10:42".
  const nowLabel = useMemo(() => {
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }, [now]);


  /** Resolve a click on the grid body to a snapped Date. */
  const handleGridClick = (e, d) => {
    if (isMediator) return; // read-only
    // v77.99.25 — Ignore grid clicks that fire AT the end of a resize
    // drag. PointerUp on the resize handle bubbles to the grid; we
    // don't want that bubble to also open the create dialog.
    if (resizeStateRef.current?.justFinished) {
      resizeStateRef.current.justFinished = false;
      return;
    }
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const minutesIntoWindow = Math.max(0, (y / rect.height) * TOTAL_MIN);
    const snapped = snapDown(WINDOW_START_MIN + minutesIntoWindow);
    const start = new Date(d);
    start.setHours(Math.floor(snapped / 60), snapped % 60, 0, 0);
    onEmptySlotClick(start);
  };

  // v77.99.25 — Drag-to-resize state + handlers.
  //
  // Design philosophy (driven by the user's "stability over fragile
  // gestures" emphasis):
  //   • Long-press (300ms) to ARM the resize. Stops a casual scroll
  //     gesture from accidentally extending an event.
  //   • Pointer Events (not mouse/touch separately) so the same code
  //     paths cover finger, stylus, mouse, trackpad.
  //   • The original end time is preserved until the user lifts the
  //     pointer — so they can drag past the snap-target and back, or
  //     even cancel mid-drag with Esc.
  //   • Live ghost overlay shows the would-be new end time AND a
  //     duration delta ("+30 min") so the user always knows exactly
  //     what they're about to commit to.
  //   • On release we DO NOT save directly — we open the existing
  //     EditOccurrenceDialog with the new end pre-filled. For
  //     recurring events the user picks scope (single / future /
  //     series). For one-offs they just confirm. That keeps audit
  //     trail, recurrence logic, and the recurrence-scope picker as
  //     the ONLY save path on this surface — zero risk of bypassing
  //     existing safeguards.
  //   • Minimum new duration of 15 minutes (snap granularity). Drag
  //     UP to shorten; the handle never lets you collapse the event
  //     to zero.

  const [resize, setResize] = useState(null);
  // v77.99.25 — `resize` is BOTH state (so React re-renders the
  // preview overlay) AND a ref (so pointer-move handlers can read
  // the latest value synchronously without a stale closure). The
  // long-press timer is set inside the handler's closure, so by the
  // time it fires the closure's `resize` is still null; without
  // this ref, the next pointer-move sees null and does nothing.
  const resizeStateRef = useRef({ justFinished: false, current: null });
  const longPressTimerRef = useRef(null);

  // Cleanup on unmount — no stale timers.
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  const beginResize = (ev, day, gridEl, pointerId) => {
    if (isMediator) return;
    const rect = gridEl.getBoundingClientRect();
    const start = new Date(ev.start);
    const end = ev.end ? new Date(ev.end) : new Date(start.getTime() + 60 * 60 * 1000);
    const r = {
      eventId: ev.id,
      ev,
      day,
      gridEl,
      gridTop: rect.top,
      gridHeight: rect.height,
      origStartMin: minSinceMidnight(start),
      origEndMin: minSinceMidnight(end),
      newEndMin: minSinceMidnight(end),
      pointerId,
    };
    resizeStateRef.current.current = r;
    setResize(r);
    // v77.99.25 — Attach window-level pointermove + pointerup listeners
    // for the duration of the drag. The handle itself is only ~6px
    // tall — long-distance vertical drags move the cursor well outside
    // it (into the day column), and column elements have no pointer
    // handlers. Listening at the window means we never lose track of
    // the pointer regardless of which element it's currently over.
    const onMove = (e) => {
      if (resizeStateRef.current.current) {
        // preventDefault on touch moves stops the page from scrolling
        // *while* the user is resizing. Outside of resize the user
        // can still scroll freely because these listeners only exist
        // for the duration of one drag.
        e.preventDefault();
        updateResize(e);
      }
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      finishResize();
    };
    const onCancel = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      cancelResize();
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  };

  /** PointerMove during resize — recompute end minutes. */
  const updateResize = (e) => {
    // Read from ref so we never see a stale closure value.
    const r = resizeStateRef.current.current;
    if (!r) return;
    const y = e.clientY - r.gridTop;
    const minutesIntoWindow = Math.max(
      0,
      Math.min(TOTAL_MIN, (y / r.gridHeight) * TOTAL_MIN),
    );
    let newEndMin = snapDown(WINDOW_START_MIN + minutesIntoWindow);
    // Snap to nearest 15 instead of just rounding down — the user is
    // explicitly dragging here so round to nearest is friendlier.
    const remainder = (WINDOW_START_MIN + minutesIntoWindow) - newEndMin;
    if (remainder >= SNAP_MIN / 2) newEndMin += SNAP_MIN;
    // Don't allow the end to collapse below start + 15 min.
    if (newEndMin < r.origStartMin + SNAP_MIN) {
      newEndMin = r.origStartMin + SNAP_MIN;
    }
    if (newEndMin !== r.newEndMin) {
      r.newEndMin = newEndMin;
      setResize({ ...r });
    }
  };

  /** PointerUp — commit by handing the new end up to CalendarPage. */
  const finishResize = () => {
    const r = resizeStateRef.current.current;
    if (!r) return;
    const { ev, day, origEndMin, newEndMin } = r;
    const changed = newEndMin !== origEndMin;
    resizeStateRef.current.current = null;
    setResize(null);
    resizeStateRef.current.justFinished = true;
    if (!changed) return;
    const newEnd = new Date(day);
    newEnd.setHours(Math.floor(newEndMin / 60), newEndMin % 60, 0, 0);
    const newEndIso = newEnd.toISOString();
    if (onEventResize) onEventResize(ev, newEndIso);
  };

  const cancelResize = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    resizeStateRef.current.current = null;
    setResize(null);
  };

  // Escape mid-drag = cancel.
  useEffect(() => {
    if (!resize) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") cancelResize();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [resize]);

  return (
    <div
      // v77.99.27 — `min-w-0` on the outer wrapper lets the
      // `overflow-x-auto` child (`week-scroll`) actually engage on
      // narrow viewports. Without it, browsers honour the inner
      // `min-w-[720px]` and let the whole container grow past its
      // parent's content box, which on a 390px mobile viewport
      // pushed `document.scrollWidth` to 754 — the page itself
      // would scroll horizontally instead of just the week grid.
      className="border border-[#D9E1E3] bg-white rounded-md overflow-hidden min-w-0 w-full"
      data-testid="week-grid"
    >
      {/* Header: prev / week label / next */}
      <div className="flex items-center justify-between p-3 border-b border-[#D9E1E3]">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setWeekOffset(weekOffset - 1)}
          data-testid="week-prev"
          aria-label="Previous week"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div
          className="font-heading text-base font-medium flex items-center gap-2"
          data-testid="week-label"
        >
          {weekLabel}
          {/* v77.99.28 — "Jump to now" pill. Only renders when this
              week contains today AND the current minute falls inside
              the 06:00-22:00 window. Tapping it smooth-scrolls the
              page so the live "now" line sits centred in the
              viewport — turns "what's happening right now" into one
              tap from anywhere on the page. */}
          {showNowPill && (
            <button
              type="button"
              onClick={handleJumpToNow}
              data-testid="week-jump-to-now"
              aria-label={`Jump to now (${nowLabel})`}
              className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-[#1F9FA4] text-white text-[11px] font-medium hover:bg-[#116B6E] active:bg-[#0B4F52] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1F9FA4]/60"
            >
              <Clock className="w-3 h-3" strokeWidth={2.2} aria-hidden="true" />
              <span className="font-mono-data">Now · {nowLabel}</span>
            </button>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setWeekOffset(weekOffset + 1)}
          data-testid="week-next"
          aria-label="Next week"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Outer wrapper enables horizontal scroll on narrow viewports.
          On <640px each day column gets a small minimum width so the
          grid stays readable without crushing events.
          v77.99.27: `max-w-full` clamps this container to its parent's
          content box so the inner `min-w-[720px]` triggers the
          *internal* scrollbar instead of pushing the page wider. */}
      <div className="overflow-x-auto max-w-full" data-testid="week-scroll">
        <div className="min-w-[720px]">
          {/* Day-of-week header */}
          <div
            className="grid border-b border-[#D9E1E3] bg-[#EEF3F4]"
            style={{ gridTemplateColumns: "60px repeat(7, minmax(0, 1fr))" }}
          >
            <div className="px-2 py-2 text-[10px] uppercase tracking-[0.08em] text-[#6F828A]"></div>
            {weekDates.map((d, i) => {
              const isToday =
                d.toDateString() === new Date().toDateString();
              return (
                <div
                  key={d.toISOString()}
                  className={`px-2 py-2 text-center border-l border-[#D9E1E3] ${isToday ? "bg-white" : ""}`}
                  data-testid={`week-day-header-${dayKey(d)}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.08em] text-[#6F828A]">
                    {DAY_LABELS[i]}
                  </div>
                  <div
                    className={`text-sm ${isToday ? "font-semibold text-[#116B6E]" : "text-[#2E3A3F]"}`}
                  >
                    {d.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* The body — single absolutely-positioned container per day.
              Total height is fixed so the % math always lines up. */}
          <div
            className="relative grid"
            style={{
              gridTemplateColumns: "60px repeat(7, minmax(0, 1fr))",
              height: `${(WINDOW_END_MIN - WINDOW_START_MIN) / 60 * 56}px`, // 56px per hour
            }}
          >
            {/* Time gutter */}
            <div className="border-r border-[#D9E1E3]">
              {HOURS.map((h, i) => (
                <div
                  key={h}
                  className="relative text-[10px] uppercase tracking-[0.08em] text-[#6F828A] pl-2"
                  style={{
                    height: `${100 / (HOURS.length - 1)}%`,
                    boxSizing: "border-box",
                  }}
                >
                  {i < HOURS.length - 1 ? (
                    <span className="absolute -top-1.5">{`${String(h).padStart(2, "0")}:00`}</span>
                  ) : null}
                </div>
              ))}
            </div>

            {/* 7 day columns */}
            {weekDates.map((d) => {
              const k = dayKey(d);
              const blocksForDay = blocks[k] || [];
              const isToday =
                d.toDateString() === new Date().toDateString();
              return (
                <div
                  key={k}
                  className={`relative border-l border-[#D9E1E3] cursor-pointer ${isToday ? "bg-[#F8FCFC]" : "bg-white"}`}
                  onClick={(e) => handleGridClick(e, d)}
                  data-testid={`week-col-${k}`}
                >
                  {/* Hour grid lines */}
                  {HOURS.slice(0, -1).map((h, i) => (
                    <div
                      key={h}
                      className="absolute left-0 right-0 border-t border-[#EEF3F4]"
                      style={{ top: `${(i / (HOURS.length - 1)) * 100}%` }}
                      aria-hidden="true"
                    />
                  ))}
                  {/* 15-min sub-band hairlines (very subtle — present
                      so a future drag-resize can snap visibly). */}
                  {HOURS.slice(0, -1).flatMap((h, i) => [
                    <div
                      key={`${h}-q1`}
                      className="absolute left-0 right-0 border-t border-dashed border-[#F4F8F9]"
                      style={{
                        top: `${(i / (HOURS.length - 1)) * 100 + (1 / (HOURS.length - 1)) * 25}%`,
                      }}
                      aria-hidden="true"
                    />,
                    <div
                      key={`${h}-q2`}
                      className="absolute left-0 right-0 border-t border-dashed border-[#F4F8F9]"
                      style={{
                        top: `${(i / (HOURS.length - 1)) * 100 + (1 / (HOURS.length - 1)) * 50}%`,
                      }}
                      aria-hidden="true"
                    />,
                    <div
                      key={`${h}-q3`}
                      className="absolute left-0 right-0 border-t border-dashed border-[#F4F8F9]"
                      style={{
                        top: `${(i / (HOURS.length - 1)) * 100 + (1 / (HOURS.length - 1)) * 75}%`,
                      }}
                      aria-hidden="true"
                    />,
                  ])}

                  {/* v77.99.28 — Live "now" indicator. Only drawn in
                      today's column AND only when the current minute
                      sits inside the 06:00-22:00 window. The ref is
                      passed to the "Jump to now" pill in the header
                      so a single tap calls scrollIntoView() on this
                      element — no DOM query needed, no stale
                      coordinates. */}
                  {isToday && isNowInWindow && nowTopPct !== null && (
                    <div
                      ref={nowLineRef}
                      data-testid="week-now-line"
                      aria-label={`Current time ${nowLabel}`}
                      className="absolute left-0 right-0 z-20 pointer-events-none"
                      style={{ top: `${nowTopPct}%` }}
                    >
                      <div className="relative h-0">
                        {/* Bullet on the leading edge so users notice the line
                            even at the densest hour. */}
                        <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-[#D14343]" />
                        <div className="absolute left-1 right-0 top-0 h-px bg-[#D14343]" />
                      </div>
                    </div>
                  )}

                  {/* Events */}
                  {blocksForDay.map(
                    ({ ev, topPct, heightPct, isClippedStart, isClippedEnd }, idx) => {
                      const startTime = fmtTime(
                        minSinceMidnight(new Date(ev.start)) % (24 * 60),
                      );
                      // v77.99.25 — When this specific block is being
                      // resized, override its height with the live
                      // pointer-y position so the user sees a
                      // continuous preview rather than a stale chip
                      // + ghost overlay.
                      const isResizing = resize?.eventId === ev.id && resize?.day === d;
                      const liveHeightPct = isResizing
                        ? Math.max(
                            MIN_EVENT_HEIGHT_PCT,
                            ((resize.newEndMin - Math.max(WINDOW_START_MIN, minSinceMidnight(new Date(ev.start)))) /
                              TOTAL_MIN) *
                              100,
                          )
                        : heightPct;
                      const liveEndTime = isResizing ? fmtTime(resize.newEndMin) : null;
                      const deltaMin = isResizing
                        ? resize.newEndMin - resize.origEndMin
                        : 0;
                      return (
                        <div
                          key={`${ev.id}-${idx}`}
                          className="absolute left-1 right-1"
                          style={{
                            top: `${topPct}%`,
                            height: `${liveHeightPct}%`,
                            minHeight: "18px",
                          }}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              if (isResizing) {
                                e.stopPropagation();
                                return;
                              }
                              e.stopPropagation();
                              onEventClick(ev, d);
                            }}
                            className={`absolute inset-0 rounded-sm border text-left px-1.5 py-1 overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1F9FA4] ${typeClass(ev.type, parentIdx(ev.parent_id))} ${isResizing ? "ring-2 ring-[#1F9FA4] shadow-md z-10" : ""}`}
                            data-testid={`week-event-${ev.id}`}
                            aria-label={`${TYPE_LABEL[ev.type] || ev.type}: ${ev.title} at ${startTime}`}
                          >
                            <div className="flex items-center gap-1 text-[10px] font-medium leading-tight">
                              {ev.recurrence && <Repeat className="w-2.5 h-2.5 flex-shrink-0" />}
                              {ev.is_shared === false ? (
                                <Lock className="w-2.5 h-2.5 flex-shrink-0" />
                              ) : (
                                <Eye className="w-2.5 h-2.5 flex-shrink-0" />
                              )}
                              <span className="font-mono-data">
                                {startTime}
                                {isResizing && (
                                  <span className="ml-1">
                                    → {liveEndTime}
                                  </span>
                                )}
                              </span>
                            </div>
                            {liveHeightPct > 5 && !isResizing && (
                              <div className="text-[11px] font-medium truncate mt-0.5 leading-tight">
                                {ev.title}
                              </div>
                            )}
                            {isResizing && (
                              <div className="text-[11px] font-medium mt-0.5 leading-tight">
                                {deltaMin >= 0 ? "+" : ""}
                                {deltaMin} min
                              </div>
                            )}
                            {liveHeightPct > 10 && childSwatches(ev.child_ids).length > 0 && !isResizing && (
                              <div className="flex gap-0.5 mt-1">
                                {childSwatches(ev.child_ids).slice(0, 4).map((c) => (
                                  <span
                                    key={c.id}
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ backgroundColor: c.color || "#1F9FA4" }}
                                    aria-hidden="true"
                                  />
                                ))}
                              </div>
                            )}
                            {(isClippedStart || isClippedEnd) && (
                              <div className="absolute right-1 bottom-0.5 text-[8px] uppercase tracking-[0.08em] opacity-60">
                                {isClippedStart && isClippedEnd ? "all-day" : isClippedStart ? "↑" : "↓"}
                              </div>
                            )}
                          </button>

                          {/* v77.99.25 — Resize handle.
                              4px tall bar at the bottom edge; expands
                              to a 14px tall touch target via the
                              pseudo-:before so fingers have room.
                              Only renders for parents (not mediators)
                              and only when the event has an end (i.e.
                              we know the original duration). Long-
                              press (300ms) arms the resize; sub-300ms
                              press lets the user scroll the page
                              past the event without resize triggering. */}
                          {!isMediator && ev.end && (
                            <div
                              role="separator"
                              aria-orientation="horizontal"
                              aria-label="Resize event end time"
                              data-testid={`week-event-resize-handle-${ev.id}`}
                              onPointerDown={(e) => {
                                if (e.button && e.button !== 0) return;
                                e.stopPropagation();
                                // Long-press timer — arms resize 300ms
                                // in; anything faster is treated as a
                                // scroll and the press is dropped.
                                const grid = e.currentTarget.closest('[data-testid^="week-col-"]');
                                const pid = e.pointerId;
                                longPressTimerRef.current = setTimeout(() => {
                                  beginResize(ev, d, grid, pid);
                                  longPressTimerRef.current = null;
                                }, 300);
                              }}
                              onPointerMove={(e) => {
                                // v77.99.25 — Read from refs (not the
                                // closure-captured state) so a recent
                                // long-press that fired beginResize
                                // is reflected here even before React
                                // re-renders this closure.
                                if (resizeStateRef.current.current) {
                                  e.preventDefault();
                                  updateResize(e);
                                } else if (longPressTimerRef.current) {
                                  // Movement before the long-press
                                  // fires — cancel so a scroll doesn't
                                  // turn into a resize.
                                  clearTimeout(longPressTimerRef.current);
                                  longPressTimerRef.current = null;
                                }
                              }}
                              onPointerUp={() => {
                                if (longPressTimerRef.current) {
                                  clearTimeout(longPressTimerRef.current);
                                  longPressTimerRef.current = null;
                                  return;
                                }
                                finishResize();
                              }}
                              onPointerCancel={cancelResize}
                              className="absolute left-2 right-2 bottom-[-2px] h-1.5 rounded-full bg-[#1F9FA4]/0 hover:bg-[#1F9FA4] active:bg-[#116B6E] cursor-ns-resize transition-colors touch-none before:absolute before:inset-x-0 before:-top-2 before:h-5 before:content-['']"
                              style={{
                                background: isResizing ? "#116B6E" : undefined,
                              }}
                            >
                              <GripHorizontal
                                className="absolute left-1/2 -translate-x-1/2 -top-[5px] w-3 h-3 text-white opacity-0 group-hover:opacity-100"
                                aria-hidden="true"
                              />
                            </div>
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              );
            })}
          </div>

          {/* Tiny footer hint — only ever needs to be read once. */}
          <div className="px-3 py-2 text-[10px] text-[#6F828A] border-t border-[#D9E1E3] bg-[#FAFBFC]">
            Tap any empty slot to add an event at that time (snapped to the
            nearest 15 minutes). Tap an event to open its details. Times
            outside 06:00–22:00 are marked with a small arrow.
          </div>
        </div>
      </div>
    </div>
  );
}

export default WeekView;
